import { useRouter } from 'next/router'
import pMap from 'p-map'
import { useMemo, useState } from 'react'

import type { CreateModeratedEntityResult } from '@/server/services/moderation/types'

import { useHasuraContext } from '@/client/context/hasura'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import { POSTS_QUERY } from '@/client/services/hasura'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import getMentionedUserIds from '@/client/services/richText/getMentionedUserIds'
import isEmpty from '@/client/services/richText/isEmpty'
import type { SerializedRichText } from '@/client/services/richText/types'
import uploadFile from '@/client/services/userUpload/uploadFile'
import type { ChannelType, LiveHelpInfo } from '@/client/services/worlds'

import { usePlatformLayoutContext } from '@/components/Layout/context'
import type { RichTextEditorSimpleRef } from '@/components/UI/RichTextEditor/RichTextEditorSimple/types'
import useRequestNotificationsModal from '@/components/Worlds/Notification/useRequestNotificationsModal'

export default function usePostComposer({
  channel,
  body,
  setBody,
  isAnnouncementPost = false,
  shouldNotifyUsers,
  shouldEmailUsers,
  editorRef,
  onPostBlocked,
  onClose,
  onPostCreated,
  expandPostAfterCreate = false,
}: {
  channel: { id: number; type: ChannelType }
  body: SerializedRichText
  setBody: (body: SerializedRichText) => void
  isAnnouncementPost?: boolean
  shouldNotifyUsers: boolean
  shouldEmailUsers: boolean
  editorRef: React.RefObject<RichTextEditorSimpleRef>
  onPostBlocked: (message: string) => void
  onClose?: () => void
  onPostCreated?: (
    createPostResult: CreateModeratedEntityResult<{ postId: number }>
  ) => Promise<void>
  expandPostAfterCreate?: boolean
}): {
  handleCreatePost: ({ liveHelpInfo }: { liveHelpInfo?: LiveHelpInfo }) => Promise<void>
  isCreatingPost: boolean
  fileUploadStatus: { uploaded: number; total: number } | null
  setFileUploadStatus: (status: { uploaded: number; total: number } | null) => void
  isBodyEmpty: boolean
  RequestNotificationsModal: () => JSX.Element
} {
  const router = useRouter()
  const hasuraContext = useHasuraContext()

  const { showRequestNotificationsModal, RequestNotificationsModal } = useRequestNotificationsModal(
    {
      body: 'We recommend turning on notifications so we can let you know as soon as a tutor is ready to help you!',
    }
  )

  const { toggleExpandPostId } = usePlatformLayoutContext()

  const [fileUploadStatus, setFileUploadStatus] = useState<{
    uploaded: number
    total: number
  } | null>(null)

  const isBodyEmpty = useMemo(() => isEmpty(body), [body])

  const [handleCreatePost, isCreatingPost] = useAPIWrapper(
    async ({ liveHelpInfo }: { liveHelpInfo?: LiveHelpInfo }) => {
      const uploads = editorRef.current?.getUploads() ?? []

      const attachmentUrls = []

      if (uploads.length > 0) {
        let filesUploaded = 0
        setFileUploadStatus({ uploaded: filesUploaded, total: uploads.length })
        const newAttachmentUrls = await pMap(
          uploads,
          async file => {
            const { downloadUrl } = await uploadFile({
              file,
              fileType: file.type,
              target: { type: 'attachment' },
            })
            filesUploaded += 1
            setFileUploadStatus({ uploaded: filesUploaded, total: uploads.length })
            return downloadUrl
          },
          { concurrency: 3 }
        )
        attachmentUrls.push(...newAttachmentUrls)
      }

      const createPostResult = await RPC.createPost({
        channelId: channel.id,
        body,
        attachmentUrls,
        liveHelpInfo,
        postType: isAnnouncementPost ? 'announcement' : 'user-post',
        shouldNotifyUsers,
        shouldEmailUsers,
        mentionedUserIds: getMentionedUserIds(body),
      })

      setFileUploadStatus(null)

      if (createPostResult.status === 'blocked') {
        onPostBlocked(createPostResult.message)
        return
      }

      if (createPostResult.status === 'suspended') {
        // Force reload to make suspended view show
        router.reload()
        return
      }

      editorRef.current?.clear()
      onClose?.()
      setBody(EMPTY_RICH_TEXT_VALUE)

      const maybeGoToPost = () => {
        if (createPostResult?.status !== 'success' || !expandPostAfterCreate) {
          return
        }
        toggleExpandPostId(createPostResult.postId)
      }

      if (liveHelpInfo != null && liveHelpInfo.requestZoomHelp) {
        showRequestNotificationsModal({
          onComplete: maybeGoToPost,
        })
      } else {
        maybeGoToPost()
      }

      await hasuraContext?.apollo.refetchQueries({
        include: [POSTS_QUERY],
      })

      if (createPostResult.status === 'success') {
        await onPostCreated?.(createPostResult)
      }
    },
    [
      editorRef,
      channel.id,
      body,
      isAnnouncementPost,
      shouldNotifyUsers,
      shouldEmailUsers,
      onClose,
      setBody,
      hasuraContext?.apollo,
      onPostBlocked,
      router,
      expandPostAfterCreate,
      toggleExpandPostId,
      showRequestNotificationsModal,
      onPostCreated,
    ],
    'Create Post'
  )

  return {
    handleCreatePost,
    isCreatingPost,
    fileUploadStatus,
    setFileUploadStatus,
    isBodyEmpty,
    RequestNotificationsModal,
  }
}
